import React, { useState, useEffect, lazy, Suspense } from "react";
import { isNativeApp, sendToNative } from "./lib/native";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import "@/App.css";

import Navigation from "@/components/Navigation";
import FloatingNav from "@/components/FloatingNav";
import LandingPage from "@/components/LandingPage";
import ScrollToTop from "@/components/ScrollToTop";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NotificationProvider } from "@/hooks/useNotifications";
import { initGA, setUserProperties } from "@/lib/analytics";
import { usePageTracking, useClickTracking } from "@/hooks/useAnalytics";

const NewsFeed = lazy(() => import("@/components/NewsFeed"));
const NewsDetail = lazy(() => import("@/components/NewsDetail"));
const Bookmarks = lazy(() => import("@/components/Bookmarks"));
const ReadingHistory = lazy(() => import("@/components/ReadingHistory"));
const Profile = lazy(() => import("@/components/Profile"));
const Library = lazy(() => import("./components/Library"));
const Creators = lazy(() => import("./components/Creators"));
const UserProfile = lazy(() => import("./components/UserProfile"));
const AuthModal = lazy(() => import("@/components/AuthModal"));
const OAuthCallback = lazy(() => import("./components/OAuthCallback"));
const Notifications = lazy(() => import("@/components/Notifications"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AppContext = React.createContext();

function AppContent() {
  const [user, setUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  usePageTracking();
  useClickTracking();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const response = await axios.get(`${API}/v1/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      setUserProperties(response.data.id, {
        username: response.data.username,
      });
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  };

  const handleAuthSuccess = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    setShowAuth(false);
    navigate("/news");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/");
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#F9F9F9] dark:bg-[#121212]">
        <div className="animate-pulse text-xl text-gray-900 dark:text-white">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      {user ? (
        <NotificationProvider>
          <Navigation user={user} onLogout={handleLogout} />
          <FloatingNav user={user} />

          <main id="main-content">
            <Suspense
              fallback={
                <div className="flex min-h-0 flex-1 items-center justify-center bg-[#F9F9F9] dark:bg-[#121212]">
                  <div className="animate-pulse text-gray-400">Loading…</div>
                </div>
              }
            >
              <Routes>
                <Route path="/auth/callback" element={<OAuthCallback />} />
                <Route path="/callback" element={<OAuthCallback />} />
                <Route
                  path="/auth/google/callback"
                  element={<OAuthCallback />}
                />
                <Route path="/" element={<Navigate to="/news" />} />
                <Route path="/news" element={<NewsFeed user={user} />} />
                <Route
                  path="/news/:newsId"
                  element={<NewsDetail user={user} />}
                />
                <Route path="/bookmarks" element={<Bookmarks user={user} />} />
                <Route
                  path="/history"
                  element={<ReadingHistory user={user} />}
                />
                <Route path="/library" element={<Library user={user} />} />
                <Route path="/profile" element={<Profile user={user} />} />
                <Route
                  path="/profile/:userId"
                  element={<UserProfile user={user} />}
                />
                <Route path="/creators" element={<Creators user={user} />} />
                <Route
                  path="/notifications"
                  element={<Notifications user={user} />}
                />
              </Routes>
            </Suspense>
          </main>
        </NotificationProvider>
      ) : (
        <main id="main-content">
          <Suspense
            fallback={
              <div className="flex min-h-0 flex-1 items-center justify-center bg-[#F9F9F9] dark:bg-[#121212]">
                <div className="animate-pulse text-gray-400">Loading…</div>
              </div>
            }
          >
            <Routes>
              <Route path="/auth/callback" element={<OAuthCallback />} />
              <Route path="/callback" element={<OAuthCallback />} />
              <Route path="/auth/google/callback" element={<OAuthCallback />} />
              <Route
                path="/"
                element={<LandingPage onGetStarted={() => setShowAuth(true)} />}
              />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </main>
      )}

      {showAuth && (
        <Suspense fallback={null}>
          <AuthModal
            onClose={() => setShowAuth(false)}
            onSuccess={handleAuthSuccess}
          />
        </Suspense>
      )}
    </div>
  );
}

function App() {
  useEffect(() => {
    if (isNativeApp()) {
      sendToNative("web_ready");
    }
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
export { API };
